import React from 'react';
import { BundleIssue, BundleReport, MaterialBundlePayload } from '../../../api/bundle';
import { IssueRow } from '../../../components/wizard/WizardShell';

// 向导第 ⑤ 步: 整包预检报告 + 发布选项 + 告警确认.
// 报告三栏按严重度分, 每条可点「去修改」跳回它所属的那一步并高亮字段.

interface Props {
    payload: MaterialBundlePayload;
    patch: (p: Partial<MaterialBundlePayload['material']>) => void;
    report: BundleReport | null;
    checking: boolean;
    ack: boolean;
    setAck: (v: boolean) => void;
    canPublish: boolean;
    onGoto: (issue: BundleIssue) => void;
    /** 提交结果 (工单号 / 本地直接创建条数) */
    done: { taskCode?: string; created?: number } | null;
}

function Group({
    title,
    tone,
    issues,
    onGoto,
}: {
    title: string;
    tone: string;
    issues: BundleIssue[];
    onGoto?: (i: BundleIssue) => void;
}) {
    if (issues.length === 0) return null;
    return (
        <div className="space-y-1.5">
            <div className={`text-xs font-semibold ${tone}`}>
                {title} ({issues.length})
            </div>
            {issues.map((i, k) => (
                <IssueRow key={`${i.code}-${i.path}-${k}`} issue={i} onGoto={onGoto} />
            ))}
        </div>
    );
}

function StepReview({
    payload,
    patch,
    report,
    checking,
    ack,
    setAck,
    canPublish,
    onGoto,
    done,
}: Props) {
    if (done) {
        return (
            <div className="card p-6 space-y-3 text-center">
                <div className="text-2xl">✅</div>
                <div className="text-sm font-semibold">
                    {done.taskCode ? '已提交审批' : '创建成功'}
                </div>
                {done.taskCode ? (
                    <div className="text-xs text-slate-500">
                        工单号 <span className="font-mono">{done.taskCode}</span> ·
                        管理员通过后整包一次落库
                    </div>
                ) : (
                    <div className="text-xs text-slate-500">
                        已创建 {done.created} 个材料套件
                    </div>
                )}
            </div>
        );
    }

    return (
        <>
            <div className="card p-4 space-y-3">
                <div className="text-sm font-semibold">
                    发布设置
                    <span className="text-rose-500"> *</span>
                </div>
                {/* 发布由提交人在这里拍板 (2026-09-17): 审批人只负责批准或驳回, 不再替你改发布状态。
                    做成必选的二选一而不是默认关闭的勾选框 —— 发布决定这份数据对下游可不可见,
                    该被明确回答一次, 而不是"没注意到就默认不发布"。 */}
                <div className="space-y-2">
                    {(
                        [
                            [
                                true,
                                '创建后立即发布',
                                '审批通过即对 OpenAPI 可见, 下游按 updated_at 增量同步会取到。',
                            ],
                            [
                                false,
                                '暂不发布',
                                '先建成未发布, 确认无误后再到材料页单独发布。',
                            ],
                        ] as [boolean, string, string][]
                    ).map(([val, label, hint]) => {
                        const disabled = val && !canPublish;
                        return (
                            <label
                                key={String(val)}
                                className={`flex items-start gap-2 text-xs rounded border px-3 py-2 ${
                                    disabled
                                        ? 'cursor-not-allowed border-slate-100 opacity-60'
                                        : 'cursor-pointer ' +
                                          (payload.material.publish === val
                                              ? 'border-brand-200 bg-brand-50'
                                              : 'border-slate-200 hover:bg-slate-50')
                                }`}
                            >
                                <input
                                    type="radio"
                                    className="mt-0.5"
                                    name="wizard-publish"
                                    disabled={disabled}
                                    checked={!!payload.material.publish === val}
                                    onChange={() => patch({ publish: val })}
                                />
                                <span>
                                    {label}
                                    <span className="block text-[11px] text-slate-400">
                                        {disabled
                                            ? '你没有材料发布权限 (material:publish), 需要发布请在工单说明里注明'
                                            : hint}
                                    </span>
                                </span>
                            </label>
                        );
                    })}
                </div>
            </div>

            <div className="card p-4 space-y-3">
                <div className="text-sm font-semibold">预检报告</div>
                {checking && <div className="text-xs text-slate-400">预检中…</div>}
                {!checking && !report && (
                    <div className="text-xs text-slate-400">尚未预检</div>
                )}
                {report && (
                    <>
                        <div
                            className={`text-xs rounded px-3 py-2 ${
                                report.ok
                                    ? 'bg-emerald-50 text-emerald-700'
                                    : 'bg-rose-50 text-rose-700'
                            }`}
                        >
                            {report.ok ? '✔ 没有阻断错误' : '✖ 存在阻断错误, 修完才能提交'} ·
                            错误 {report.errors.length} · 告警 {report.warnings.length} · 提示{' '}
                            {report.infos.length}
                        </div>
                        <Group
                            title="错误"
                            tone="text-rose-600"
                            issues={report.errors}
                            onGoto={onGoto}
                        />
                        <Group
                            title="告警"
                            tone="text-amber-600"
                            issues={report.warnings}
                            onGoto={onGoto}
                        />
                        <Group title="提示" tone="text-slate-500" issues={report.infos} />
                        {report.warnings.length > 0 && (
                            <label className="flex items-center gap-2 text-xs cursor-pointer border-t border-slate-100 pt-3">
                                <input
                                    type="checkbox"
                                    checked={ack}
                                    onChange={(e) => setAck(e.target.checked)}
                                />
                                我已知晓上述 {report.warnings.length} 条告警, 确认提交
                            </label>
                        )}
                    </>
                )}
            </div>
        </>
    );
}

export default StepReview;
